import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { profitIntelligenceDev109Api, type Dev109Status, type SprintDashboard, type SprintValidation } from '../../api/profit-intelligence-dev109';

type Tab = 'overview' | 'modules' | 'providers' | 'jobs' | 'gates' | 'status';

export function ProfitIntelligenceDev109Page() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<SprintDashboard | null>(null);
  const [modules, setModules] = useState<{ key: string; name: string; category: string }[]>([]);
  const [providers, setProviders] = useState<{ key: string; name: string; interface: string }[]>([]);
  const [jobs, setJobs] = useState<{ key: string; name: string; schedule?: string }[]>([]);
  const [gates, setGates] = useState<{ key: string; name: string; blocking: boolean }[]>([]);
  const [status, setStatus] = useState<Dev109Status | null>(null);
  const [validation, setValidation] = useState<SprintValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const card = 'border-amber-200 bg-white dark:border-amber-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-amber-700 dark:text-amber-300';

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mod, prov, jb, gt, st] = await Promise.all([
        profitIntelligenceDev109Api.dashboard(), profitIntelligenceDev109Api.intelligenceModules(),
        profitIntelligenceDev109Api.providers(), profitIntelligenceDev109Api.backgroundJobs(),
        profitIntelligenceDev109Api.qualityGates(), profitIntelligenceDev109Api.status(),
      ]);
      setDashboard(dash); setModules(mod); setProviders(prov); setJobs(jb); setGates(gt); setStatus(st);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load sprint center'); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validate = async () => {
    setBusy(true); setActionMsg(null);
    try {
      const result = await profitIntelligenceDev109Api.validate({
        true_profit_operational: true, leakage_detection_operational: true,
        settlement_reconciliation_operational: true, recovery_workflows_operational: true,
        recommendations_from_dna: true, experiments_auto_created: true,
        marketplace_dna_updated: true, financial_forecasts_measurable: true,
        all_apis_documented: true, tests_pass: true, ci_passes: true, performance_budgets_achieved: true,
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Profit Intelligence passes all quality gates' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Validation failed'); }
    finally { setBusy(false); }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'modules', label: 'Modules' }, { id: 'providers', label: 'Providers' },
    { id: 'jobs', label: 'Jobs' }, { id: 'gates', label: 'Quality Gates' }, { id: 'status', label: 'Status' },
  ];

  return (
    <div className="space-y-6 rounded-xl bg-amber-50/20 p-1 dark:bg-zinc-950">
      <div>
        <p className={`text-xs font-semibold uppercase ${accent}`}>DEV-000109 · P0 · Financial Intelligence & Leakage Detection</p>
        <h1 className="text-2xl font-semibold">Profit Intelligence & Leakage Detection Engine</h1>
        <p className={`text-sm ${muted}`}>Observe → Calculate → Reconcile → Detect → Explain → Recover → Learn</p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          <Link to="/platform/fba-growth-intelligence-dev108" className={`${accent} hover:underline`}>DEV-000108</Link>
          <span className={muted}>→</span>
          <Link to="/profit/B0CX123456/intelligence" className={`${accent} hover:underline`}>Live Profit Workspace</Link>
          <Link to="/platform/profit-intelligence-leakage-engine-prd" className={`${accent} hover:underline`}>PRD-000007</Link>
        </div>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-amber-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Not Accounting — Continuous Profit Optimization</h2>
            <p className={`mt-2 text-sm ${muted}`}>Maximize net profit, cash flow and contribution margin while minimizing marketplace leakages, fee errors and unclaimed reimbursements. Every recommendation originates from Marketplace DNA.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-4">
            {[{ label: 'Objectives', value: dashboard.objectives }, { label: 'Modules', value: dashboard.intelligence_modules },
              { label: 'Providers', value: dashboard.provider_interfaces }, { label: 'Quality Gates', value: dashboard.quality_gates }].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>{m.label}</p><p className="text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
          <button type="button" disabled={busy} onClick={validate} className="rounded-lg bg-amber-600 px-4 py-2 text-sm text-white disabled:opacity-50">Validate Quality Gates</button>
          {validation && <p className={`text-sm ${validation.valid ? 'text-amber-600' : 'text-red-600'}`}>{validation.valid ? 'All gates passed' : validation.gaps?.join(', ')}</p>}
        </div>
      )}

      {tab === 'modules' && <div className="grid gap-2 sm:grid-cols-3">{modules.map((m) => (
        <div key={m.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{m.name}</p><p className={`text-xs ${muted}`}>{m.category}</p></div>
      ))}</div>}

      {tab === 'providers' && <div className="grid gap-2 sm:grid-cols-2">{providers.map((p) => (
        <div key={p.key} className={`rounded-xl border p-4 ${card}`}><p className="font-semibold">{p.name}</p><p className={`text-xs font-mono ${muted}`}>{p.interface}</p></div>
      ))}</div>}

      {tab === 'jobs' && <div className="space-y-2">{jobs.map((j) => (
        <div key={j.key} className={`flex justify-between rounded-xl border p-3 ${card}`}><span>{j.name}</span><span className={`text-xs ${muted}`}>{j.schedule}</span></div>
      ))}</div>}

      {tab === 'gates' && <div className="space-y-2">{gates.map((g) => (
        <div key={g.key} className={`flex justify-between rounded-xl border p-3 ${card}`}><span>{g.name}</span>{g.blocking && <span className="text-xs text-amber-600">blocking</span>}</div>
      ))}</div>}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <p>Sprint: {status.sprint_id} · {status.status}</p>
          <p className={`text-sm ${muted}`}>Production ready: {String(status.production_ready)} · DNA integrated: {String(status.dna_integrated)}</p>
        </div>
      )}
    </div>
  );
}
